import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  Session,
  SessionStatus as SessionStatusEnum,
  useCSMRecommendations,
  useRecruitment,
  useTaskSettingsAvailabilities,
  QuotaRef,
  useQuotas,
  StudyType,
  useCountParticipationByStatus,
  ParticipationStatus,
} from '@app/api';
import { SessionBoxInfo } from 'components/Session/SessionBoxInfo';

import { FormatDetailsModal } from './FormatDetailsModal';

import { SessionBillingDetailsModal } from './SessionBillingDetailsModal/SessionBillingDetailsModal';
import { GenericButton, ProgressLine, useBoolModalState } from '@app/core';
import { SessionStatus } from './SessionStatus';
import { useTranslation } from 'react-i18next';
import { RecommendationClient } from './RecommendationClient';
import { BillingBox } from './BillingBox';
import { QuotaBox } from './QuotaBox';
import { QuotaBoxHelp } from './QuotaBoxHelp';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import { SessionNotification } from './SessionNotification';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  sessionBoxesLine: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    width: 240,
    alignItems: 'center',
    padding: [theme.marginBase * 2, 0],
  },
  box: {
    flex: 1,
    width: '100%',
    marginBottom: theme.marginBase * 2,
  },
  formatBoxContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& > i': {
      fontSize: theme.iconSize.large,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'left',
    padding: [theme.marginBase, 0],
    marginBottom: theme.marginBase * 2,
  },
  title: {
    ...theme.fonts.h5,
    textTransform: 'uppercase',
    color: theme.colors.inkSecondary,
  },
  collapseColor: {
    color: theme.colors.inkSecondary,
  },
}));

interface Props {
  session: Session;
  taggedCriteria?: string[];
  onAddQuotaItemTag?: (criterion: QuotaRef) => any;
  onRemoveQuotaItemTag?: (criterion: QuotaRef) => any;
}

export const SessionBoxColumn = ({
  session,
  taggedCriteria,
  onAddQuotaItemTag,
  onRemoveQuotaItemTag,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const navigate = useNavigate();
  const [formatDetailsIsOpened, setFormatDetailsIsOpened] = useState(false);
  const [quotaIsCollapsed, setQuotaIsCollapsed] = useState(false);
  const [recommendationIsCollapsed, setRecommendationIsCollapsed] =
    useState(true);
  const { t } = useTranslation();
  const { data: participationsCountByStatuses } = useCountParticipationByStatus(
    session.id,
  );

  const handleQuotaCollapseButton = useCallback(() => {
    setQuotaIsCollapsed(!quotaIsCollapsed);
  }, [quotaIsCollapsed]);
  const handleRecommendationCollapseButton = useCallback(() => {
    setRecommendationIsCollapsed(!recommendationIsCollapsed);
  }, [recommendationIsCollapsed]);

  const { data: recruitment } = useRecruitment(session.recruitmentId);
  const { data: recommendations } = useCSMRecommendations(session.id);
  const { data: availabilities } = useTaskSettingsAvailabilities(
    session.taskSettingId,
  );
  const { data: quotas } = useQuotas(recruitment?.sessionId);

  const haveQuotas = useMemo(() => {
    return (
      recruitment?.criteria?.find((c) => !!c.quotas?.length) || !!quotas?.length
    );
  }, [quotas?.length, recruitment?.criteria]);

  const noFutureAvailability = useMemo(() => {
    return !availabilities?.find((a) => moment(a.end).isAfter(moment()));
  }, [availabilities]);

  const showTimeslotAlert = useMemo(() => {
    return (
      session.studyType === StudyType.MODERATED &&
      session.status &&
      ![SessionStatusEnum.FULL, SessionStatusEnum.CLOSED].includes(
        session.status,
      ) &&
      !availabilities?.find((a) =>
        moment(a.end).isAfter(moment().add(3, 'days')),
      )
    );
  }, [session, availabilities]);

  const tooMuchReview = useMemo(() => {
    if (
      !participationsCountByStatuses ||
      !participationsCountByStatuses[ParticipationStatus.APPLIED]
    ) {
      return false;
    }
    return (
      participationsCountByStatuses[ParticipationStatus.APPLIED] >=
      (session.numberOfParticipantRequired - session.numberOfBooked) * 2
    );
  }, [participationsCountByStatuses]);

  const [
    isBillingDetailsModalOpen,
    openBillingDetailsModal,
    closeBillingDetailsModal,
  ] = useBoolModalState(false);

  return (
    <React.Fragment>
      <div className={classes.sessionBoxesLine}>
        <div className={classes.titleContainer}>
          <h1 className={classes.title}>
            {t('sessions.details.monitoring.columnTitle.overview')}
          </h1>
        </div>

        <SessionStatus session={session} className={classes.box} />
        {((recommendations && recommendations?.length > 0) ||
          tooMuchReview ||
          showTimeslotAlert) && (
          <SessionBoxInfo
            color="red"
            title={t('sessions.details.boxes.notification')}
            className={classes.box}
            icon="ri-notification-2-line"
            pin={true}
            actionButton={
              <GenericButton
                onClick={handleRecommendationCollapseButton}
                className={classes.collapseColor}
              >
                <i
                  className={
                    recommendationIsCollapsed
                      ? 'ri-expand-vertical-line'
                      : 'ri-collapse-vertical-line'
                  }
                ></i>
              </GenericButton>
            }
            isCollapsed={recommendationIsCollapsed}
          >
            {showTimeslotAlert && (
              <SessionNotification
                message={
                  noFutureAvailability
                    ? t('sessions.details.notification.timeslot.noMore')
                    : t('sessions.details.notification.timeslot.almostNoMore')
                }
                color="red"
                icon="ri-calendar-event-line"
                onClick={() =>
                  navigate(`/sessions/edit/${session.id}/schedule`)
                }
                withNextArrow
                buttonText={t('sessions.details.notification.timeslot.button')}
              />
            )}
            {tooMuchReview && (
              <SessionNotification
                message={t(
                  'sessions.details.monitoring.informationCard.review',
                )}
                color="red"
                icon="ri-error-warning-line"
              />
            )}
            {recommendations && recommendations?.length > 0 && (
              <RecommendationClient
                recommendations={recommendations}
                sessionId={session.id}
              />
            )}
          </SessionBoxInfo>
        )}
        {!!session.numberOfParticipantRequired && (
          <SessionBoxInfo
            color="blue"
            title={t('sessions.details.boxes.recruitTitle')}
            className={classes.box}
            icon="ri-user-3-line"
          >
            <ProgressLine
              current={session.numberOfBooked}
              max={session.numberOfParticipantRequired}
              oneline
              color={
                session.status === SessionStatusEnum.DRAFT
                  ? 'inkSecondary'
                  : session.numberOfBooked ===
                    session.numberOfParticipantRequired
                  ? 'green'
                  : 'blue'
              }
            />
          </SessionBoxInfo>
        )}
        {haveQuotas && (
          <SessionBoxInfo
            color="blue"
            title={t('sessions.details.boxes.quota.title')}
            className={classes.box}
            icon="ri-pie-chart-line"
            actionButton={
              <GenericButton
                onClick={handleQuotaCollapseButton}
                className={classes.collapseColor}
              >
                <i
                  className={
                    quotaIsCollapsed
                      ? 'ri-expand-vertical-line'
                      : 'ri-collapse-vertical-line'
                  }
                ></i>
              </GenericButton>
            }
            help={<QuotaBoxHelp studyType={session.studyType} />}
            isCollapsed={quotaIsCollapsed}
          >
            <QuotaBox
              recruitment={recruitment}
              taggedCriteria={taggedCriteria}
              onAddQuotaItemTag={onAddQuotaItemTag}
              onRemoveQuotaItemTag={onRemoveQuotaItemTag}
            />
          </SessionBoxInfo>
        )}
        <BillingBox
          sessionId={session.id}
          className={classes.box}
          onClick={openBillingDetailsModal}
        />
      </div>
      <FormatDetailsModal
        isOpen={formatDetailsIsOpened}
        onRequestClose={() => setFormatDetailsIsOpened(false)}
        session={session}
      />
      <SessionBillingDetailsModal
        isOpen={isBillingDetailsModalOpen}
        onRequestClose={closeBillingDetailsModal}
        session={session}
      />
    </React.Fragment>
  );
};
